/**
 * Shared auth-failure primitives.
 *
 * Kept dependency-free so it can be imported by both the API layer
 * (`@hooks/utils/api`) and the Sentry helper (`@utils/sentry`) without
 * creating a circular import between them.
 */

/** Window event dispatched when the user's session is terminally unauthorized. */
export const SESSION_EXPIRED_EVENT = "socialpro:session-expired";

/**
 * Window event dispatched when the backend rejects a request because the user's
 * organisation requires MFA and they have not enrolled TOTP (403
 * MFA_ENROLMENT_REQUIRED). Distinct from session-expiry: the user keeps their
 * session and is routed to forced enrolment, NOT signed out.
 */
export const MFA_ENROLMENT_REQUIRED_EVENT = "socialpro:mfa-enrolment-required";

/** Dispatch the MFA-enrolment-required signal (no-op outside the browser). */
export const dispatchMfaEnrolmentRequired = (): void => {
  if (
    typeof window !== "undefined" &&
    typeof window.dispatchEvent === "function"
  ) {
    window.dispatchEvent(new Event(MFA_ENROLMENT_REQUIRED_EVENT));
  }
};

/**
 * User-facing message shown when a session expires mid-request. Owned by the
 * auth layer (`useAuthContext`), which surfaces it exactly once per expiry.
 */
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please log in again.";

/**
 * Thrown when an authenticated request is rejected as Unauthorized even after a
 * forced token refresh (or when no access token can be obtained at all).
 *
 * This is an *expected* condition — the user's Cognito session has expired or
 * been revoked — not an application bug, so it is deliberately not reported to
 * Sentry as an error (see `catchSentryError`).
 */
export class SessionExpiredError extends Error {
  /** The original underlying error (e.g. the Amplify 401), if any. */
  readonly originalError?: unknown;

  constructor(originalError?: unknown) {
    super("Session expired");
    this.name = "SessionExpiredError";
    this.originalError = originalError;
    // Restore prototype chain for `instanceof` after transpilation to ES5.
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

export const isSessionExpiredError = (
  error: unknown
): error is SessionExpiredError =>
  error instanceof SessionExpiredError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { name?: string }).name === "SessionExpiredError");

/**
 * Signal the rest of the app that the session is dead so the auth layer can
 * sign the user out and redirect to login. Safe to call in non-browser
 * environments (e.g. tests/SSR) where `window` is unavailable.
 */
export const dispatchSessionExpired = (): void => {
  if (
    typeof window !== "undefined" &&
    typeof window.dispatchEvent === "function"
  ) {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
};

/**
 * True when this page load is a hosted-UI / SAML callback still carrying an
 * authorization code for Amplify to exchange.
 *
 * Deliberately `code` only. An `?error=` callback has nothing to exchange and
 * no session coming, so waiting on it would just park the user on the loader
 * until the timeout fell through.
 */
export const hasPendingOAuthCode = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return new URLSearchParams(window.location.search).has("code");
};
